import { ReactElement, useEffect, useRef, useState } from 'react'
import { useClickOutsideDetector } from 'common'
import styled from 'styled-components'
import { ArrowDropDown, ArrowDropUp, Delete } from '@mui/icons-material'
import { Overlay } from 'react-bootstrap'
import { Box, Grow, IconButton } from '@mui/material'
import { zIndex } from '../../../../src/theme/zIndex'

interface SingleOption {
  label: string
  value: string
}
interface SortSelectProps {
  hasDelete?: boolean
  onChange: (value: string) => void
  value?: string
  options: Array<SingleOption>
  disabled?: boolean
  cursorType: string
  width?: string
}
export const SortSelect = (sortProps: SortSelectProps): ReactElement => {
  const {
    hasDelete,
    onChange,
    value: defaultValue,
    options,
    disabled,
    width,
    cursorType,
  } = sortProps
  const [active, setActive] = useState(false)
  const [selectedValue, setSelectedValue] = useState<string | null>(null)
  const fieldRef = useRef(null)
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setSelectedValue(defaultValue)
  }, [defaultValue])

  const onSelectItem = (value: string | null) => {
    setSelectedValue(value)
    const option = options.filter((item) => item.value === value)[0].value
    onChange(option)
    setActive(false)
  }
  const isSelected = (value: string): boolean => {
    return value === selectedValue
  }
  const renderLabel = () => {
    if (!active && selectedValue) {
      return options.filter((item) => item.value === selectedValue)?.[0]?.label
    }
    return null
  }

  useClickOutsideDetector([fieldRef, overlayRef], active, () =>
    setActive(false)
  )

  return (
    <>
      <FieldButtonWrapper
        ref={fieldRef}
        onClick={() => {
          if (disabled) {
            return
          }
          setActive(!active)
        }}
      >
        <FieldButton disabled={disabled} width={width} cursorType={cursorType}>
          <Label>{renderLabel()}</Label>
          {active ? (
            <ArrowDropUp htmlColor="#000" />
          ) : (
            <ArrowDropDown htmlColor="#000" />
          )}
        </FieldButton>
      </FieldButtonWrapper>
      {hasDelete && (
        <IconButton onClick={() => onSelectItem(null)} className="ml-0 pl-0">
          <Delete style={{ color: 'black' }} />
        </IconButton>
      )}
      <Overlay
        show={active}
        placement="bottom-start"
        popperConfig={{ strategy: 'absolute' }}
        target={fieldRef.current}
      >
        {({ ...props }) => (
          <Wrapper
            {...props}
            style={{
              ...props.style,
              zIndex: zIndex.dropdown,
            }}
          >
            <Box>
              <Grow in>
                <InnerWrapper ref={overlayRef}>
                  {options.map((option) => (
                    <Option
                      key={option?.value}
                      selected={isSelected(option?.value)}
                      onClick={() => onSelectItem(option?.value)}
                    >
                      {option?.label}
                    </Option>
                  ))}
                </InnerWrapper>
              </Grow>
            </Box>
          </Wrapper>
        )}
      </Overlay>
    </>
  )
}

interface FieldButtonProps {
  disabled: boolean
  width: string
  cursorType: string
}

interface OptionProps {
  selected: boolean
}

const Label = styled.span`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  text-align: left;
  color: #050505;
`
const Wrapper = styled.div`
  display: inline-block;
  background: white;
  width: 220px;
`
const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  background-color: #fff;
  color: ${(p) => p.theme.colors.text};
  border-radius: 3px;
  border: ${(p) => p.theme.fieldBorder};
`

const FieldButton = styled.span<FieldButtonProps>`
  border: ${(props) => props.theme.fieldBorder};
  display: flex;
  font-size: 14px;
  background-color: ${(p) =>
    p.disabled ? p.theme.colors.disabledBackground : '#FFF'};
  align-items: center;
  justify-content: space-between;
  height: 48px;
  padding-left: 16px;
  padding-right: 16px;
  border-radius: 4px;
  width: ${(p) => (p.width ? p.width : '222px')};
  margin-right: 0;
  cursor: ${(p) =>
    p.cursorType !== 'pointer'
      ? p.cursorType
      : p.disabled
        ? null
        : p.cursorType};
`

const FieldButtonWrapper = styled.span`
  display: flex;
  flex-grow: 1;
  position: relative;
`
const Option = styled.div<OptionProps>`
  padding: 10px;
  cursor: pointer;
  background-color: ${(p) => (p.selected ? p.theme.colors.primary : 'none')};
  &:hover {
    background-color: ${(p) =>
      p.selected ? p.theme.colors.primary : '#f3f2ee'};
  }
  &:active {
    background-color: ${(p) =>
      p.selected ? p.theme.colors.primary : '#777777'};
  }
`
